import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { ArrowLeft, Save, Trash2, Search, UserPlus, UserMinus, ChevronDown, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';
import { facultyApi } from '../api/facultyApi';
import { parseApiError } from '../lib/apiErrors';

function AddMembersPicker({ groupId, existingIds, onAdded, onClose }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const runSearch = useCallback(() => {
    setLoading(true);
    adminApi.listStudents({ search, registration_status: 'approved', is_active: true, page_size: 20 })
      .then(({ data }) => setResults(data.data.filter((s) => !existingIds.has(s.id))))
      .catch(() => toast.error('Failed to search students'))
      .finally(() => setLoading(false));
  }, [search, existingIds]);

  useEffect(() => {
    const t = setTimeout(runSearch, 250);
    return () => clearTimeout(t);
  }, [runSearch]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      await adminApi.addGroupMembers(groupId, [...selected]);
      toast.success(`Added ${selected.size} member(s)`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to add members').message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">Add Members</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={20} />
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search approved students by name, email, or phone..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
            data-testid="member-picker-search"
          />
        </div>
        <div className="flex-1 overflow-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-700">
          {loading ? (
            <p className="p-4 text-sm text-slate-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-slate-400">No matching approved students.</p>
          ) : (
            results.map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30"
                data-testid={`picker-student-${s.id}`}
              >
                <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} className="rounded" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{s.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {s.email}{s.class_level ? ` · Class ${s.class_level}` : ''}
                  </p>
                </div>
              </label>
            ))
          )}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
            data-testid="picker-add-btn"
          >
            <UserPlus size={16} /> {adding ? 'Adding…' : `Add Selected (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddFacultyPicker({ groupId, existingIds, onAdded, onClose }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const runSearch = useCallback(() => {
    setLoading(true);
    facultyApi.getAll({ search, status: 'active', page_size: 20 })
      .then(({ data }) => setResults(data.data.filter((f) => !existingIds.has(f.id))))
      .catch(() => toast.error('Failed to search faculty'))
      .finally(() => setLoading(false));
  }, [search, existingIds]);

  useEffect(() => {
    const t = setTimeout(runSearch, 250);
    return () => clearTimeout(t);
  }, [runSearch]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      await adminApi.addGroupFaculty(groupId, [...selected]);
      toast.success(`Assigned ${selected.size} faculty member(s)`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to assign faculty').message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">Assign Faculty</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={20} />
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search faculty by name, email, or phone..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
            data-testid="faculty-picker-search"
          />
        </div>
        <div className="flex-1 overflow-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-700">
          {loading ? (
            <p className="p-4 text-sm text-slate-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-slate-400">No matching faculty.</p>
          ) : (
            results.map((f) => (
              <label
                key={f.id}
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30"
                data-testid={`faculty-picker-${f.id}`}
              >
                <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} className="rounded" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{f.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{f.email}</p>
                </div>
              </label>
            ))
          )}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
            data-testid="faculty-picker-add-btn"
          >
            <UserPlus size={16} /> {adding ? 'Assigning…' : `Assign Selected (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminGroupDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [formerMembers, setFormerMembers] = useState([]);
  const [formerOpen, setFormerOpen] = useState(false);
  const [assignedFaculty, setAssignedFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [facultyPickerOpen, setFacultyPickerOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminApi.listGroups(),
      adminApi.listGroupMembers(id),
      adminApi.listFormerGroupMembers(id),
      adminApi.listGroupFaculty(id),
    ])
      .then(([groupsRes, membersRes, formerRes, facultyRes]) => {
        const g = groupsRes.data.find((x) => x.id === id);
        if (!g) {
          toast.error('Group not found');
          navigate('/admin/groups');
          return;
        }
        setGroup(g);
        setName(g.name);
        setDescription(g.description || '');
        setMembers(membersRes.data);
        setFormerMembers(formerRes.data);
        setAssignedFaculty(facultyRes.data);
      })
      .catch(() => toast.error('Failed to load group'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.updateGroup(id, { name: name.trim(), description: description.trim() || null });
      setGroup(data);
      toast.success('Group updated');
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(parseApiError(err, 'A group with this name already exists').message);
      } else {
        toast.error(parseApiError(err, 'Failed to update group').message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsLeft = async (student) => {
    if (!window.confirm(
      `Mark ${student.name} as left "${group.name}"?\n\n` +
      `Use this for graduation, transfer, or course finished. They leave the current ` +
      `roster, but their past scores and attendance in this group stay exactly as ` +
      `they were. You can re-add them later via Add Members to undo this.`
    )) return;
    try {
      await adminApi.markGroupMemberLeft(id, student.id);
      setMembers((prev) => prev.filter((m) => m.id !== student.id));
      setFormerMembers((prev) => [
        { ...student, left_on: new Date().toISOString().slice(0, 10) },
        ...prev,
      ]);
      setGroup((prev) => (prev ? { ...prev, member_count: Math.max(0, prev.member_count - 1) } : prev));
      toast.success(`${student.name} marked as left`);
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to mark as left').message);
    }
  };

  const handleRemoveAddedInError = async (student) => {
    if (!window.confirm(
      `Remove ${student.name} from "${group.name}" — added in error?\n\n` +
      `This is for a data-entry mistake only: the student never actually belonged to ` +
      `this group. This PERMANENTLY DELETES their membership record and cannot be ` +
      `undone. If the student is actually leaving (graduated, transferred), use ` +
      `"Mark as left" instead.`
    )) return;
    try {
      await adminApi.removeGroupMemberAddedInError(id, student.id);
      setMembers((prev) => prev.filter((m) => m.id !== student.id));
      setGroup((prev) => (prev ? { ...prev, member_count: Math.max(0, prev.member_count - 1) } : prev));
      toast.success('Member removed');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to remove member').message);
    }
  };

  const handleRemoveFaculty = async (faculty) => {
    if (!window.confirm(`Remove ${faculty.name} from "${group.name}"?`)) return;
    try {
      await adminApi.removeGroupFaculty(id, faculty.id);
      setAssignedFaculty((prev) => prev.filter((f) => f.id !== faculty.id));
      toast.success('Faculty removed');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to remove faculty').message);
    }
  };

  const existingIds = new Set(members.map((m) => m.id));
  const existingFacultyIds = new Set(assignedFaculty.map((f) => f.id));

  return (
    <DashboardLayout title="Group Detail">
      <div className="animate-fade-in max-w-4xl space-y-6">
        <button
          onClick={() => navigate('/admin/groups')}
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-cool transition-colors"
        >
          <ArrowLeft size={18} /> Back to Groups
        </button>

        {loading || !group ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-32 rounded-lg bg-slate-200 dark:bg-slate-700" />
            <div className="h-64 rounded-lg bg-slate-200 dark:bg-slate-700" />
          </div>
        ) : (
          <>
            <form onSubmit={handleSave} className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    data-testid="group-detail-name"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving || !name.trim()}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-medium text-white hover:bg-primary-light disabled:opacity-50"
                >
                  <Save size={18} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>

            <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 overflow-hidden">
              <div className="flex items-center justify-between p-6 pb-4">
                <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">
                  Members ({members.length})
                </h3>
                <button
                  onClick={() => setPickerOpen(true)}
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2 text-sm transition-colors duration-200 font-medium"
                  data-testid="open-add-members-btn"
                >
                  <UserPlus size={16} /> Add Members
                </button>
              </div>
              {members.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">No members yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                      <th className="px-5 py-3 text-left font-medium text-slate-500">Name</th>
                      <th className="px-5 py-3 text-left font-medium text-slate-500">Class</th>
                      <th className="px-5 py-3 text-left font-medium text-slate-500">Status</th>
                      <th className="px-5 py-3 text-right font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {members.map((m) => (
                      <tr key={m.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-testid={`member-row-${m.id}`}>
                        <td className="px-5 py-3">
                          <p className="font-medium text-slate-900 dark:text-white">{m.name}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{m.email}</p>
                        </td>
                        <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{m.class_level ?? '—'}</td>
                        <td className="px-5 py-3 text-slate-500 dark:text-slate-400 capitalize">{m.registration_status}</td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleMarkAsLeft(m)}
                            className="p-2 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors text-amber-600"
                            title="Mark as left (graduated / transferred — keeps their history)"
                            data-testid={`mark-left-${m.id}`}
                          >
                            <UserMinus size={16} />
                          </button>
                          <button
                            onClick={() => handleRemoveAddedInError(m)}
                            className="p-2 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors text-rose-500"
                            title="Remove — added in error (permanently deletes)"
                            data-testid={`remove-error-${m.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <button
                onClick={() => setFormerOpen((prev) => !prev)}
                className="flex w-full items-center gap-2 border-t border-slate-100 dark:border-slate-700 px-6 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                data-testid="toggle-former-members"
              >
                {formerOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                Former members ({formerMembers.length})
              </button>
              {formerOpen && (
                formerMembers.length === 0 ? (
                  <div className="px-6 py-6 text-center text-slate-400 text-sm border-t border-slate-100 dark:border-slate-700">
                    No former members. Students who are marked as left will appear here.
                  </div>
                ) : (
                  <table className="w-full text-sm border-t border-slate-100 dark:border-slate-700" data-testid="former-members-table">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                        <th className="px-5 py-3 text-left font-medium text-slate-500">Name</th>
                        <th className="px-5 py-3 text-left font-medium text-slate-500">Class</th>
                        <th className="px-5 py-3 text-left font-medium text-slate-500">Left on</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {formerMembers.map((m) => (
                        <tr key={m.id} className="text-slate-500 dark:text-slate-400" data-testid={`former-member-row-${m.id}`}>
                          <td className="px-5 py-3">
                            <p className="font-medium text-slate-700 dark:text-slate-300">{m.name}</p>
                            <p className="text-sm">{m.email}</p>
                          </td>
                          <td className="px-5 py-3">{m.class_level ?? '—'}</td>
                          <td className="px-5 py-3">{m.left_on ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 overflow-hidden">
              <div className="flex items-center justify-between p-6 pb-4">
                <div>
                  <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">
                    Assigned Faculty ({assignedFaculty.length})
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    These faculty can mark attendance for this group's students.
                  </p>
                </div>
                <button
                  onClick={() => setFacultyPickerOpen(true)}
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2 text-sm transition-colors duration-200 font-medium"
                  data-testid="open-add-faculty-btn"
                >
                  <UserPlus size={16} /> Assign Faculty
                </button>
              </div>
              {assignedFaculty.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">No faculty assigned yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                      <th className="px-5 py-3 text-left font-medium text-slate-500">Name</th>
                      <th className="px-5 py-3 text-left font-medium text-slate-500">Email</th>
                      <th className="px-5 py-3 text-right font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {assignedFaculty.map((f) => (
                      <tr key={f.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-testid={`faculty-row-${f.id}`}>
                        <td className="px-5 py-3 font-medium text-slate-900 dark:text-white">{f.name}</td>
                        <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{f.email}</td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => handleRemoveFaculty(f)}
                            className="p-2 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors text-rose-500"
                            title="Remove from group"
                            data-testid={`remove-faculty-${f.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </>
        )}
      </div>

      {pickerOpen && (
        <AddMembersPicker
          groupId={id}
          existingIds={existingIds}
          onAdded={load}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {facultyPickerOpen && (
        <AddFacultyPicker
          groupId={id}
          existingIds={existingFacultyIds}
          onAdded={load}
          onClose={() => setFacultyPickerOpen(false)}
        />
      )}
    </DashboardLayout>
  );
}
